import type { AppLocale } from "./locale";
import type { Catalog } from "./types";

const CATALOG_ID_PATTERN = /^[a-f\d]{12}$/;
const SHARED_CATALOG_START_PATTERN =
  /^(?:rescue_)?catalog_([a-f\d]{12})_(\d{1,4})$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const CATALOG_LIBRARY_START_PARAMETER = "catalogs";
export const RESCUE_CATALOG_LIBRARY_START_PARAMETER = "rescue_catalogs";

export interface SharedCatalogSnapshot {
  id: string;
  chain: string;
  title: string;
  valid_from: string;
  valid_until: string;
  page: number;
  pages: number;
}

export interface SharedCatalogReference {
  catalogId: string;
  page: number;
}

export interface PreparedCatalogShare {
  preparedMessageId: string;
  preparedMessageExpiresAt: number | null;
  shareUrl: string;
}

export class SharedCatalogApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
  ) {
    super(code);
  }
}

function boundedString(value: unknown, maximum: number): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= maximum;
}

export function normalizeSharedCatalogSnapshot(
  value: unknown,
): SharedCatalogSnapshot | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const candidate = value as Partial<SharedCatalogSnapshot>;
  if (
    typeof candidate.id !== "string" ||
    !CATALOG_ID_PATTERN.test(candidate.id) ||
    !boundedString(candidate.chain, 80) ||
    !boundedString(candidate.title, 160) ||
    typeof candidate.valid_from !== "string" ||
    !DATE_PATTERN.test(candidate.valid_from) ||
    typeof candidate.valid_until !== "string" ||
    !DATE_PATTERN.test(candidate.valid_until) ||
    candidate.valid_until < candidate.valid_from ||
    typeof candidate.page !== "number" ||
    !Number.isInteger(candidate.page) ||
    candidate.page < 1 ||
    typeof candidate.pages !== "number" ||
    !Number.isInteger(candidate.pages) ||
    candidate.pages < 1 ||
    candidate.pages > 2_000 ||
    candidate.page > candidate.pages
  ) {
    return null;
  }
  return {
    id: candidate.id,
    chain: candidate.chain,
    title: candidate.title,
    valid_from: candidate.valid_from,
    valid_until: candidate.valid_until,
    page: candidate.page,
    pages: candidate.pages,
  };
}

export function createSharedCatalogSnapshot(
  catalog: Catalog,
  page: number,
): SharedCatalogSnapshot {
  return {
    id: catalog.id,
    chain: catalog.chain,
    title: catalog.title,
    valid_from: catalog.valid_from,
    valid_until: catalog.valid_until,
    page,
    pages: catalog.pages,
  };
}

export function sharedCatalogStartParameter(
  catalogId: string,
  page: number,
): string {
  return `catalog_${catalogId}_${page}`;
}

export function rescueCatalogStartParameter(
  catalogId: string,
  page: number,
): string {
  return `rescue_catalog_${catalogId}_${page}`;
}

export function sharedCatalogTelegramUrl(
  catalogId: string,
  page: number,
  botUsername = "KiloPrice_bot",
): string {
  return `https://t.me/${botUsername}?startapp=${sharedCatalogStartParameter(catalogId, page)}`;
}

export function sharedCatalogPageImagePath(
  catalogId: string,
  page: number,
): string | null {
  if (
    !CATALOG_ID_PATTERN.test(catalogId) ||
    !Number.isInteger(page) ||
    page < 1 ||
    page > 2_000
  ) {
    return null;
  }
  return `/generated/catalog-pages/${catalogId}/page-${String(page).padStart(3, "0")}.jpg`;
}

export function parseSharedCatalogStartParameter(
  value: string | null,
): SharedCatalogReference | null {
  if (!value) {
    return null;
  }
  const match = SHARED_CATALOG_START_PATTERN.exec(value);
  if (!match) {
    return null;
  }
  const page = Number.parseInt(match[2] ?? "", 10);
  return page >= 1 && page <= 2_000
    ? { catalogId: match[1]!, page }
    : null;
}

export function isCatalogLibraryStartParameter(
  value: string | null,
): boolean {
  return (
    value === CATALOG_LIBRARY_START_PARAMETER ||
    value === RESCUE_CATALOG_LIBRARY_START_PARAMETER
  );
}

async function responseBody(response: Response): Promise<Record<string, unknown>> {
  try {
    return (await response.json()) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export async function prepareCatalogShare(
  initData: string,
  catalog: SharedCatalogSnapshot,
  locale: AppLocale,
): Promise<PreparedCatalogShare> {
  const response = await fetch("/api/share-catalog", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ initData, catalog, locale }),
  });
  const body = await responseBody(response);
  if (!response.ok) {
    throw new SharedCatalogApiError(
      typeof body.error === "string" ? body.error : "share_failed",
      response.status,
    );
  }
  if (
    typeof body.preparedMessageId !== "string" ||
    typeof body.shareUrl !== "string"
  ) {
    throw new SharedCatalogApiError("invalid_share_response", 502);
  }
  return {
    preparedMessageId: body.preparedMessageId,
    preparedMessageExpiresAt:
      typeof body.preparedMessageExpiresAt === "number"
        ? body.preparedMessageExpiresAt
        : null,
    shareUrl: body.shareUrl,
  };
}
